import { createOnnxSession } from "@instacover/helpers"
import { convertPredictionsToLabels, imgFromFile, isNotNil, Maybe, Tail } from "@instacover/utils"
import { useQuery } from "@tanstack/react-query"
import type { InferenceSession, Tensor } from "onnxruntime-web"
import { useCallback } from "react"

import { queryKeys } from "@/constants/queryKeys"
import { logger } from "@/utils/logging"

import { MODEL_QUALITY_LABELS, MODEL_VIEW_LABELS, OnnxModelResult, PredictResponse } from "./modelTypes"
import { imgToTensor } from "./modelUtils"

// This will act as a singleton for the model URL used across the app, instead of using react context
// currently no other url is needed, but this can be used later together with setOnnxModelUrl fn
const DEFAULT_MODEL_URL = "https://public.instacover.ai/instahomeModels/model-4648e5dd93d34025a445390cb492d72b.onnx"
let activeModelUrl = DEFAULT_MODEL_URL

export function setOnnxModelUrl(url: string) {
  activeModelUrl = url
}

export const loadOnnxModelOptions = () => ({
  queryKey: queryKeys.onnxModel(activeModelUrl),
  // warm up model even when user is offline, as model is already downloaded, if its not downloaded yet it would fail anyways
  networkMode: "always" as const,
  queryFn: async () => {
    try {
      const loadStart = new Date().valueOf()
      const { session, Tensor } = await createOnnxSession(activeModelUrl)
      const loadEnd = new Date().valueOf() - loadStart

      // warm up model by running a dummy inference
      const warmUpStart = new Date().valueOf()
      const arr = new Array(299 * 299 * 3).fill(0)
      const tensor = new Tensor(Int32Array.from(arr), [1, 299, 299, 3])
      await session.run({ input: tensor })
      const warmUpEnd = new Date().valueOf() - warmUpStart

      return { model: session, loadTime: loadEnd, warmUpTime: warmUpEnd }
    } catch (error) {
      logger.error(error, "Failed to load model.")
      throw error
    }
  },
})

/**
 * Loads ONNX model. Also acts as a singleton to prevent multiple model instances being loaded.
 * Url for the model is taken from activeModelUrl singleton, as to avoid using React context for passing the model url.
 * @returns Onnx model query
 */
export function useLoadOnnxModel() {
  return useQuery({
    ...loadOnnxModelOptions(),
    refetchOnMount: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    // this will prevent garbage collection of the model
    gcTime: Infinity,
    retry: Infinity,
    retryDelay: 2000,
  })
}

/**
 * Gets the model predictions and maps them to labels.
 * @param model Model instance
 * @param tensor Input tensor
 * @returns Mapped predictions
 */
async function predictMapper(model: InferenceSession, tensor: Tensor) {
  const rawResult = await model.run({ input: tensor })
  const result = rawResult as OnnxModelResult
  const viewPredictions = Array.from(result.softmax_space_type.data)
  const qualityPredictions = Array.from(result.softmax_quality.data)

  const view = convertPredictionsToLabels(viewPredictions, MODEL_VIEW_LABELS)
  const quality = convertPredictionsToLabels(qualityPredictions, MODEL_QUALITY_LABELS)

  return { view, quality }
}

/**
 * Automatically predicts the model results for the given file using the ONNX model.
 * @param model Model instance
 * @param file File to be predicted
 * @returns Prediction results
 */
async function predict(model: InferenceSession, file: File) {
  const preprocessStart = new Date().valueOf()
  const img = await imgFromFile(file)

  const tensor = await imgToTensor(img)
  const preprocessTime = new Date().valueOf() - preprocessStart
  const predictStart = new Date().valueOf()

  const { view, quality } = await predictMapper(model, tensor)
  const predictTime = new Date().valueOf() - predictStart

  return { view, quality, preprocessTime, predictTime }
}

type ModelHook =
  | {
      enabled: true
      loaded: boolean
      predict: (file: File) => Promise<PredictResponse>
      warmUpTime: number
      loadTime: number
      modelUrl: string
      error: unknown
    }
  | {
      enabled: false
      error?: unknown
    }

/**
 * Loads the ONNX model and provides a predict function, that can be manually called.
 * @returns Model hook
 */
export function useModel(): ModelHook {
  const { data: modelData, isLoading, error } = useLoadOnnxModel()

  const predictWrapper = useCallback(
    async (...args: Tail<Parameters<typeof predict>>): Promise<PredictResponse> => {
      if (!modelData?.model) {
        throw new Error("model is undefined")
      }

      const result = await predict(modelData.model, ...args)
      const confidence = result.quality.confidence ?? 0
      const isConfident = confidence >= 0.6

      return { ...result, isConfident }
    },
    [modelData?.model],
  )

  if (!modelData?.model) {
    return { enabled: false, error }
  }

  return {
    enabled: true,
    loaded: !isLoading,
    predict: predictWrapper,
    warmUpTime: modelData.warmUpTime,
    loadTime: modelData.loadTime,
    modelUrl: activeModelUrl,
    error,
  }
}

/**
 * Automatically predicts the model results for the given file using the ONNX model.
 * Prediction is triggered automatically when the file changes.
 * @param file File to be predicted
 * @returns Query with the prediction results
 */
export function useModelPredict(file: Maybe<File>) {
  const model = useModel()

  const query = useQuery({
    queryKey: queryKeys.onnxModelResult(file),
    // allow to infer photo even when offline
    networkMode: "always",
    enabled: isNotNil(file) && model.enabled && model.loaded,
    queryFn: async () => {
      if (!file || !model.enabled) {
        return null
      }

      return await model.predict(file)
    },
  })

  return query
}
